import CloseIcon from '@mui/icons-material/Close';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import {IconButton} from 'components/icon-button';
import {ModalContext} from 'components/modals/modal-context';
import React, {useContext} from 'react';
import * as ModalConstants from '../modal-constants';

import type {Theme as DefaultTheme} from '@mui/material/styles';
import type {Props as DialogModalProps} from 'components/modals/sc-modal';
import type {Property} from 'csstype';

const getMarginValue = (size: DialogModalProps['size']) => {
    if (size === 'small') {
        return ModalConstants.modalMarginSm;
    }

    if (size === 'large') {
        return ModalConstants.modalMarginLg;
    }

    return ModalConstants.modalMarginMd;
};

const useStyles = makeStyles<
    DefaultTheme,
    {
        size: DialogModalProps['size'];
    }
>((theme) => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        margin: 0,
        padding: 0,
        '& > button': {
            backgroundColor: theme.palette.common.white,
        },
    },
    closeButton: ({size}) => {
        const margin = getMarginValue(size);
        return {
            position: 'absolute' as Property.Position,
            top: `calc(${margin} - 0.4rem)`,
            right: margin,
            background: theme.palette.background.paper,
            zIndex: 1,
        };
    },
    title: ({size}) => {
        const margin = getMarginValue(size);
        return {
            ...theme.typography.componentM,
            fontWeight: theme.typography.fontWeightBold as number,
            color: theme.palette.text.secondary,
            margin: margin,
            marginBottom: ModalConstants.modalMarginSm,
        };
    },
}));

export type Props = {
    title: string;
    disableClose?: boolean | undefined;
    topImage?: React.ReactNode | undefined;
};

const ModalTitle = (props: Props) => {
    const {title, disableClose, topImage} = props;

    const {size, open, onClose} = useContext(ModalContext);
    const classes = useStyles({
        size,
    });

    return (
        <DialogTitle
            classes={{
                root: classes.root,
            }}>
            {topImage && (
                <div
                    style={{
                        width: '100%',
                    }}>
                    {topImage}
                </div>
            )}
            <Typography className={classes.title}>{title}</Typography>
            {!disableClose && (
                <IconButton
                    variant='tertiary'
                    size='small'
                    className={classes.closeButton}
                    disabled={!open}
                    aria-label='close'
                    onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            )}
        </DialogTitle>
    );
};

export default ModalTitle;
